import sqlite3InitModule from '@sqlite.org/sqlite-wasm';
import type { Order } from '../models/order';
import { OrderModel } from '../models/order';
import type { ClientOptionsResponse, ProductPack } from '../models/clientOptionsResponse';
import type { PackCategory, ProductPackCache } from '../models/productPackCache';

/**
 * Map SQLite row data to an object with named properties
 * @param columnNames Array of column names from SQLite result
 * @param row Array of values from SQLite result
 * @returns Object with column names as keys
 */
export function mapSqliteRow(columnNames: string[], row: any[]): Record<string, any> {
  const obj: Record<string, any> = {};
  columnNames.forEach((name: string, i: number) => {
    obj[name] = row[i];
  });
  return obj;
}

/**
 * Interface for models that can be instantiated from database rows
 */
export interface IModel<T> {
  fromRow(row: Record<string, any>): T;
}

/**
 * Execute a SQL query and return typed results with named properties
 * @param sql SQL query string with ? placeholders for parameters
 * @param params Optional array of parameters to bind to the query
 * @param returnMultiple If true, returns all matching rows; if false, returns single row
 * @param ModelClass Optional model class with fromRow method for transformation
 * @returns Typed result(s) or null if no results
 */
export async function querySqlite<T = any>(
  sql: string,
  params?: any[],
  returnMultiple: boolean = false,
  ModelClass?: { fromRow(row: Record<string, any>): T }
): Promise<T | T[] | null> {
  await waitForDB();
  if (!db) {
    throw new Error('Database not initialized');
  }
  
  // Use rowMode: 'object' to get rows as objects with named properties
  const result = db.exec({
    sql,
    bind: params || [],
    returnValue: 'resultRows',
    rowMode: 'object'
  });
  
  //console.log("querySqlite result:", result);
  
  if (!result || result.length === 0) {
    // Check if this was a modification query and persist if needed
    const isModification = /^\s*(INSERT|UPDATE|DELETE|REPLACE|CREATE|DROP|ALTER)/i.test(sql);
    if (isModification) {
      await persistDB();
    }
    return null;
  }
  
  // Transform using model class if provided
  const transformedRows = ModelClass 
    ? result.map((row: any) => ModelClass.fromRow(row as Record<string, any>))
    : result as T[];
  
  // Check if this was a modification query and persist if needed
  const isModification = /^\s*(INSERT|UPDATE|DELETE|REPLACE|CREATE|DROP|ALTER)/i.test(sql);
  if (isModification) {
    await persistDB();
  }
  
  if (returnMultiple) {
    return transformedRows.length > 0 ? transformedRows : null;
  } else {
    return transformedRows.length > 0 ? transformedRows[0] : null;
  }
}

let db: any = null; // SQLite WASM Database
let sqlite3: any = null; // SQLite WASM module
let dbInitialized = false;
let dbInitPromise: Promise<void> | null = null;

// IndexedDB constants
const IDB_NAME = 'WalkInBathDB';
const IDB_VERSION = 1;
const IDB_STORE_NAME = 'sqlite';
const IDB_KEY = 'main';

/**
 * Open IndexedDB and ensure object store exists
 */
function openIndexedDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IDB_NAME, IDB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
        db.createObjectStore(IDB_STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

/**
 * Load SQLite database from IndexedDB
 */
async function loadDatabaseFromIndexedDB(): Promise<Uint8Array | null> {
  try {
    const idb = await openIndexedDB();
    return new Promise((resolve, reject) => {
      const transaction = idb.transaction([IDB_STORE_NAME], 'readonly');
      const store = transaction.objectStore(IDB_STORE_NAME);
      const request = store.get(IDB_KEY);
      
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data : null);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error loading database from IndexedDB:', error);
    return null;
  }
}

/**
 * Save SQLite database to IndexedDB
 */
async function saveDatabaseToIndexedDB(data: Uint8Array): Promise<void> {
  try {
    const idb = await openIndexedDB();
    return new Promise((resolve, reject) => {
      const transaction = idb.transaction([IDB_STORE_NAME], 'readwrite');
      const store = transaction.objectStore(IDB_STORE_NAME);
      const request = store.put({ id: IDB_KEY, data });
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving database to IndexedDB:', error);
    throw error;
  }
}

/*
async function persistDBOld(): Promise<void> {
  if (!dbInitialized || !db || !sqlite3) {
    return;
  }
  
  try {
    // Serialize the database to a Uint8Array
    const exportPtr = sqlite3.capi.sqlite3_js_db_export(db.pointer);
    const binaryArray = sqlite3.capi.sqlite3_js_db_export_ptr_to_uint8array(exportPtr);
    await saveDatabaseToIndexedDB(binaryArray);
    sqlite3.capi.sqlite3_free(exportPtr);
  } catch (error) {
    console.error('Error persisting database:', error);
  }
}*/
/**
 * Persist current database state to IndexedDB
 */
async function persistDB(): Promise<void> {
  if (!dbInitialized || !db || !sqlite3) {
    return;
  }
  
  try {
    // Allocate memory for the size output parameter (a 64-bit integer pointer)
    const pSize = sqlite3.wasm.alloc(8); // 8 bytes for sqlite3_int64
    
    // Serialize the database
    const pDB = sqlite3.capi.sqlite3_serialize(
      db.pointer,
      'main',
      pSize,
      0
    );
    
    if (!pDB) {
      sqlite3.wasm.dealloc(pSize);
      throw new Error('Failed to serialize database');
    }
    
    // Read the size (as a 64-bit value)
    const size = sqlite3.wasm.peek(pSize, 'i64');
    
    // Create Uint8Array copy of the serialized data
    const heapView = sqlite3.wasm.heap8u();
    const binaryArray = new Uint8Array(Number(size));
    for (let i = 0; i < Number(size); i++) {
      binaryArray[i] = heapView[pDB + i];
    }
    
    await saveDatabaseToIndexedDB(binaryArray);
    
    // Free allocated memory
    sqlite3.capi.sqlite3_free(pDB);
    sqlite3.wasm.dealloc(pSize);
  } catch (error) {
    console.error('Error persisting database:', error);
  }
}

/**
 * Clear stored database from IndexedDB (useful for debugging/reset)
 */
export async function clearStoredDB(): Promise<void> {
  try {
    const idb = await openIndexedDB();
    return new Promise((resolve, reject) => {
      const transaction = idb.transaction([IDB_STORE_NAME], 'readwrite');
      const store = transaction.objectStore(IDB_STORE_NAME);
      const request = store.delete(IDB_KEY);
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error clearing stored database:', error);
    throw error;
  }
}

export async function initDB() {
  // Return existing promise if already initializing
  if (dbInitPromise) {
    return dbInitPromise;
  }

  dbInitPromise = (async () => {
    try {
      // Initialize SQLite WASM
      sqlite3 = await sqlite3InitModule({
        print: console.log,
        printErr: console.error,
      });
      
      //console.log('SQLite WASM loaded:', sqlite3.version.libVersion);
      
      // Try to load existing database from IndexedDB
      const savedData = await loadDatabaseFromIndexedDB();
      
      if (savedData && savedData.byteLength > 0) {
        // Restore database from saved data
        const p = sqlite3.wasm.allocFromTypedArray(savedData);
        db = new sqlite3.oo1.DB();
        const rc = sqlite3.capi.sqlite3_deserialize(
          db.pointer,
          'main',
          p,
          savedData.byteLength,
          savedData.byteLength,
          sqlite3.capi.SQLITE_DESERIALIZE_FREEONCLOSE | sqlite3.capi.SQLITE_DESERIALIZE_RESIZEABLE
        );
        if (rc !== 0) {
          throw new Error(`Failed to deserialize database: ${rc}`);
        }
        //console.log('Database loaded from IndexedDB');
      } else {
        // Create new database in memory
        db = new sqlite3.oo1.DB();
        //console.log('New database created');
      }
      
      //console.log('Database init migrate start');

      // Ensure schema exists (migration-safe for both new and existing databases)
      // This allows backward compatibility when new tables are added
      

      db.exec(`
        CREATE TABLE IF NOT EXISTS orders (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          order_date TEXT,
          status TEXT,
          total_contract_price REAL DEFAULT 0,
          step_data TEXT,
          created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
          updated_at DATETIME DEFAULT CURRENT_TIMESTAMP,
          synced_at DATETIME,
          sync_version INTEGER DEFAULT 1,
          sync_status TEXT,
          last_modified TEXT,
          marked_for_deletion DATETIME
        );
        CREATE TABLE IF NOT EXISTS images (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          order_id INTEGER,
          step INTEGER,
          blob BLOB,
          filename TEXT,
          synced_at DATETIME,
          server_id TEXT,
          deleted_at DATETIME,
          tag TEXT NOT NULL DEFAULT 'Untagged'
        );
        CREATE TABLE IF NOT EXISTS client_options_sync (
          id INTEGER PRIMARY KEY CHECK (id = 1),
          last_sync TEXT NOT NULL
        );
        CREATE TABLE IF NOT EXISTS client_option_items (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          field_key TEXT NOT NULL,
          step INTEGER,
          field_value TEXT NOT NULL,
          field_title TEXT,
          price REAL
        );
        CREATE TABLE IF NOT EXISTS product_catalogue (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          type TEXT NOT NULL,
          parent TEXT NOT NULL,
          child TEXT NOT NULL
        );
        CREATE TABLE IF NOT EXISTS sync_metadata (
          id INTEGER PRIMARY KEY CHECK (id = 1),
          last_server_time TEXT NOT NULL,
          last_sync_date TEXT NOT NULL
        );
        CREATE INDEX IF NOT EXISTS idx_field_key ON client_option_items(field_key);
        CREATE INDEX IF NOT EXISTS idx_step ON client_option_items(step);
        CREATE INDEX IF NOT EXISTS idx_catalogue_parent ON product_catalogue(parent);
        CREATE TABLE IF NOT EXISTS product_incompatibility (
            id INTEGER PRIMARY KEY AUTOINCREMENT,
            option_id TEXT NOT NULL,
            incompatible_variant_id TEXT,
            incompatible_option_id TEXT
        );

        CREATE INDEX IF NOT EXISTS idx_incompat_option ON product_incompatibility(option_id);

        CREATE INDEX IF NOT EXISTS idx_incompat_variant ON product_incompatibility(incompatible_variant_id);

        CREATE INDEX IF NOT EXISTS idx_incompat_option_pair ON product_incompatibility(incompatible_option_id);


        CREATE TABLE IF NOT EXISTS product_packs (
            id INTEGER PRIMARY KEY AUTOINCREMENT,
            range_id INTEGER NOT NULL,
            pack_key TEXT NOT NULL,
            manditory INTEGER NOT NULL 
        );

        

      `);

      /*
      for boolean CHECK (manditory IN (0, 1)) DEFAULT 0
      CREATE INDEX IF NOT EXISTS idx_packs_range
            ON product_packs(range_id);

        CREATE INDEX IF NOT EXISTS idx_packs_key
            ON product_packs(pack_key); 
      */


      // Run migrations for existing databases
      // Migration: Add 'price' column to client_option_items if it doesn't exist
      try {
        const tableInfo = db.exec({
          sql: 'PRAGMA table_info(client_option_items)',
          returnValue: 'resultRows',
          rowMode: 'object'
        });
        
        const hasPriceColumn = tableInfo.some((col: any) => col.name === 'price');
        
        if (!hasPriceColumn) {
          console.log('Running migration: Adding price column to client_option_items');
          db.exec('ALTER TABLE client_option_items ADD COLUMN price REAL');
          console.log('✓ Migration completed: price column added');
        }
      } catch (migrationError) {
        console.error('Migration error (non-critical):', migrationError);
      }

      //Migration for product packs
      try {
        const tableInfo = db.exec({
          sql: 'PRAGMA table_info(product_packs)',
          returnValue: 'resultRows',
          rowMode: 'object'
        });
        

        const hasManditoryColumn = tableInfo.some((col: any) => col.name === 'manditory');
        
        if (!hasManditoryColumn) {
          console.log('Running migration: Adding manditory column to product_packs');
          db.exec('ALTER TABLE product_packs ADD COLUMN manditory INTEGER NOT NULL DEFAULT 0');
          console.log('✓ Migration completed: manditory column added');
        }
      } catch (migrationError) {
        console.error('Migration error (non-critical):', migrationError);
      }
      
      // Migration: Fix NOT NULL constraint on field_title column
      try {
        const tableInfo = db.exec({
          sql: 'PRAGMA table_info(client_option_items)',
          returnValue: 'resultRows',
          rowMode: 'object'
        });
        
        // Check if field_title has NOT NULL constraint
        const fieldTitleColumn = tableInfo.find((col: any) => col.name === 'field_title');
        
        if (fieldTitleColumn && fieldTitleColumn.notnull === 1) {
          console.log('Running migration: Recreating client_option_items table with nullable field_title and price columns');
          
          // Create new table with correct schema
          db.exec(`
            CREATE TABLE client_option_items_new (
              id INTEGER PRIMARY KEY AUTOINCREMENT,
              field_key TEXT NOT NULL,
              step INTEGER,
              field_value TEXT NOT NULL,
              field_title TEXT,
              price REAL
            );
          `);
          
          // Copy existing data
          db.exec(`
            INSERT INTO client_option_items_new (id, field_key, step, field_value, field_title, price)
            SELECT id, field_key, step, field_value, field_title, price
            FROM client_option_items;
          `);
          
          // Drop old table
          db.exec('DROP TABLE client_option_items;');
          
          // Rename new table
          db.exec('ALTER TABLE client_option_items_new RENAME TO client_option_items;');
          
          // Recreate indexes
          db.exec(`
            CREATE INDEX idx_field_key ON client_option_items(field_key);
            CREATE INDEX idx_step ON client_option_items(step);
          `);
          
          console.log('✓ Migration completed: client_option_items table recreated with nullable columns');
        }
      } catch (migrationError) {
        console.error('Migration error (non-critical):', migrationError);
      }
      
      // Migration: Add 'total_contract_price' column to orders table if it doesn't exist
      try {
        const ordersTableInfo = db.exec({
          sql: 'PRAGMA table_info(orders)',
          returnValue: 'resultRows',
          rowMode: 'object'
        });
        
        const hasTotalContractPriceColumn = ordersTableInfo.some((col: any) => col.name === 'total_contract_price');
        
        if (!hasTotalContractPriceColumn) {
          console.log('Running migration: Adding total_contract_price column to orders table');
          db.exec('ALTER TABLE orders ADD COLUMN total_contract_price REAL DEFAULT 0');
          console.log('✓ Migration completed: total_contract_price column added to orders table');
        }
      } catch (migrationError) {
        console.error('Migration error (non-critical):', migrationError);
      }
      
      // Migration: Add 'server_id' and 'deleted_at' columns to images table if they don't exist
      try {
        const imagesTableInfo = db.exec({
          sql: 'PRAGMA table_info(images)',
          returnValue: 'resultRows',
          rowMode: 'object'
        });
        
        const hasServerIdColumn = imagesTableInfo.some((col: any) => col.name === 'server_id');
        const hasDeletedAtColumn = imagesTableInfo.some((col: any) => col.name === 'deleted_at');
        const hasTagColumn = imagesTableInfo.some((col: any) => col.name === 'tag');
        
        if (!hasServerIdColumn) {
          console.log('Running migration: Adding server_id column to images table');
          db.exec('ALTER TABLE images ADD COLUMN server_id TEXT');
          console.log('✓ Migration completed: server_id column added to images table');
        }
        
        if (!hasDeletedAtColumn) {
          console.log('Running migration: Adding deleted_at column to images table');
          db.exec('ALTER TABLE images ADD COLUMN deleted_at DATETIME');
          console.log('✓ Migration completed: deleted_at column added to images table');
        }
        
        if (!hasTagColumn) {
          console.log('Running migration: Adding tag column to images table');
          db.exec('ALTER TABLE images ADD COLUMN tag TEXT NOT NULL DEFAULT "Untagged"');
          console.log('✓ Migration completed: tag column added to images table');
        }
      } catch (migrationError) {
        console.error('Migration error (non-critical):', migrationError);
      }
      
      // Migration: Add 'sync_version', 'last_modified', 'marked_for_deletion' columns to orders table if they don't exist
      try {
        const ordersTableInfo2 = db.exec({
          sql: 'PRAGMA table_info(orders)',
          returnValue: 'resultRows',
          rowMode: 'object'
        });
        
        const hasSyncVersionColumn = ordersTableInfo2.some((col: any) => col.name === 'sync_version');
        const hasSyncStatusColumn = ordersTableInfo2.some((col: any) => col.name === 'sync_status');
        const hasLastModifiedColumn = ordersTableInfo2.some((col: any) => col.name === 'last_modified');
        const hasMarkedForDeletionColumn = ordersTableInfo2.some((col: any) => col.name === 'marked_for_deletion');
        
        if (!hasSyncVersionColumn) {
          console.log('Running migration: Adding sync_version column to orders table');
          db.exec('ALTER TABLE orders ADD COLUMN sync_version INTEGER DEFAULT 1');
          console.log('✓ Migration completed: sync_version column added to orders table');
        }

        if (!hasSyncStatusColumn) {
          console.log('Running migration: Adding sync_status column to orders table');
          db.exec('ALTER TABLE orders ADD COLUMN sync_status TEXT');
          console.log('✓ Migration completed: sync_status column added to orders table');
        }
        
        if (!hasLastModifiedColumn) {
          console.log('Running migration: Adding last_modified column to orders table');
          db.exec('ALTER TABLE orders ADD COLUMN last_modified TEXT');
          console.log('✓ Migration completed: last_modified column added to orders table');
        }
        
        if (!hasMarkedForDeletionColumn) {
          console.log('Running migration: Adding marked_for_deletion column to orders table');
          db.exec('ALTER TABLE orders ADD COLUMN marked_for_deletion DATETIME');
          console.log('✓ Migration completed: marked_for_deletion column added to orders table');
        }
      } catch (migrationError) {
        console.error('Migration error (non-critical):', migrationError);
      }
      
      // Persist the database (either newly created or after migration)
      await persistDB();
      //console.log('Database schema verified and persisted to IndexedDB');
      
      dbInitialized = true;
      //console.log('✓ Database initialization completed successfully');
    } catch (error) {
      //console.error('Database initialization failed:', error);
      dbInitialized = false;
      dbInitPromise = null;
      throw error;
    }
  })();

  return dbInitPromise;
}

/**
 * Wait for database to be initialized before executing any operations
 */
export async function waitForDB(): Promise<void> {
  if (dbInitialized) {
    return;
  }
  if (dbInitPromise) {
    await dbInitPromise;
  } else {
    throw new Error('Database initialization has not been started');
  }
}

/**
 * Check if database is ready
 */
export function isDBReady(): boolean {
  return dbInitialized;
}

export async function saveOrder(orderData: Order) {
  await waitForDB();
  if (!db) {
    throw new Error('Database not initialized');
  }
  
  //console.log(`[saveOrder] Saving order ${orderData.id} with totalContractPrice: £${orderData.totalContractPrice}`);
  
  // Use exec with bound parameters - now includes sync fields
  db.exec({
    sql: `INSERT OR REPLACE INTO orders (id, order_date, status, total_contract_price, step_data, sync_version, sync_status, last_modified, updated_at)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?, datetime('now'))`,
    bind: [
      orderData.id, 
      orderData.orderDate, 
      orderData.status, 
      orderData.totalContractPrice || 0,
      JSON.stringify(orderData.step_data),
      orderData.sync_version || 1,
      orderData.sync_status,
      orderData.last_modified || null
    ]
  });
  
  // Persist changes to IndexedDB
  await persistDB();
  //console.log(`[saveOrder] Order ${orderData.id} saved successfully to database`);
}

export async function getAssignedOrders(): Promise<Order[]> {
  // Use the new querySqlite helper with OrderModel for automatic type conversion
  const orders = await querySqlite<Order>(
    'SELECT * FROM orders ORDER BY updated_at', //WHERE status != ?
    [], //['synced']
    true, // return multiple results
    OrderModel // use OrderModel.fromRow for transformation
  );
  
  //console.log("getAssignedOrders fetched orders:", orders);

  // Now we can log with named properties instead of array indices
  if (orders && Array.isArray(orders)) {
    //console.log("getAssignedOrders - Total fetched:", orders.length);
    /*orders.forEach((order) => {
      //console.log("Order status:", order.status); // Access by field name!
      //console.log("Order date:", order.orderDate);
    });*/
  }
  
  return orders as Order[] || [];
}

export async function getAssignedOrderCount(): Promise<number> {
  await waitForDB();
  if (!db) {
    throw new Error('Database not initialized');
  }
  
  // Use rowMode: 'object' to get rows as objects with named properties
  const result = db.exec({
    sql: 'SELECT COUNT(*) as count FROM orders',
    returnValue: 'resultRows',
    rowMode: 'object'
  });
  
  //console.log("getAssignedOrderCount result:", result);
  
  if (!result || result.length === 0) {
    return 0;
  }
  
  return result[0].count || 0;
}

export async function saveImage(orderId: number, step: number, file: File, tag: string) {
  await waitForDB();
  if (!db) throw new Error('Database not initialized');
  
  return new Promise<void>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        if (!db) {
          reject(new Error('Database not initialized'));
          return;
        }
        const blob = new Uint8Array(reader.result as ArrayBuffer);
        
        db.exec({
          sql: 'INSERT INTO images (order_id, step, blob, filename, tag) VALUES (?, ?, ?, ?, ?)',
          bind: [orderId, step, blob, file.name, tag]
        });
        
        // Persist changes to IndexedDB
        await persistDB();
        console.log(`[saveImage] Image saved for order ${orderId}, step ${step}, tag: ${tag}: ${file.name}`);
        resolve();
        
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Get all images for a specific order and step
 * @param orderId The order ID
 * @param step The wizard step number
 * @returns Array of image objects with id, blob data, filename, and tag
 */
export async function getImagesByOrderAndStep(orderId: number, step: number): Promise<Array<{ id: number; blob: Uint8Array; filename: string; tag: string }> | null> {
  await waitForDB();
  if (!db) throw new Error('Database not initialized');
  
  const result = db.exec({
    sql: 'SELECT id, blob, filename, tag FROM images WHERE order_id = ? AND step = ? AND deleted_at IS NULL ORDER BY id',
    bind: [orderId, step],
    returnValue: 'resultRows',
    rowMode: 'array'
  });
  
  if (!result || result.length === 0) {
    return null;
  }
  
  // Convert blob data back to Uint8Array
  const images = result.map((row: any[]) => ({
    id: row[0] as number,
    blob: new Uint8Array(row[1] as ArrayBuffer),
    filename: row[2] as string,
    tag: row[3] as string
  }));
  
  console.log(`[getImagesByOrderAndStep] Retrieved ${images.length} images for order ${orderId}, step ${step}`);
  return images;
}

/**
 * Delete a specific image by ID (soft-delete if synced, hard-delete if not)
 * If the image has a server_id (was previously synced), mark it for deletion
 * Otherwise, delete it immediately from the database
 * @param imageId The ID of the image to delete
 */
export async function deleteImage(imageId: number): Promise<void> {
  await waitForDB();
  if (!db) throw new Error('Database not initialized');
  
  // Check if image has a server_id
  const result = db.exec({
    sql: 'SELECT server_id FROM images WHERE id = ?',
    bind: [imageId],
    returnValue: 'resultRows',
    rowMode: 'object'
  });
  
  if (result && result.length > 0 && result[0].server_id) {
    // Image was synced to server - soft delete (mark for deletion)
    db.exec({
      sql: 'UPDATE images SET deleted_at = datetime("now") WHERE id = ?',
      bind: [imageId]
    });
    console.log(`[deleteImage] Image ${imageId} marked for deletion (soft delete)`);
  } else {
    // Image was never synced - hard delete immediately
    db.exec({
      sql: 'DELETE FROM images WHERE id = ?',
      bind: [imageId]
    });
    console.log(`[deleteImage] Image ${imageId} deleted immediately (hard delete)`);
  }
  
  // Persist changes to IndexedDB
  await persistDB();
}

export async function getOrderById(id: number): Promise<Order | null> {
  // Use the new querySqlite helper with OrderModel
  const order = await querySqlite<Order>(
    'SELECT * FROM orders WHERE id = ?',
    [id],
    false, // return single result
    OrderModel // use OrderModel.fromRow for transformation
  );
  
  return order as Order | null;
}

/**
 * Delete an order and its associated images from the database
 * @param orderId The ID of the order to delete
 */
export async function deleteOrder(orderId: number): Promise<void> {
  await waitForDB();
  if (!db) throw new Error('Database not initialized');
  
  // Delete associated images first (foreign key relationship)
  db.exec({
    sql: 'DELETE FROM images WHERE order_id = ?',
    bind: [orderId]
  });
  
  // Delete the order
  db.exec({
    sql: 'DELETE FROM orders WHERE id = ?',
    bind: [orderId]
  });
  
  // Persist changes to IndexedDB
  await persistDB();
}

/**
 * Execute SQL with type safety, parameters, and support for single/multiple results
 * @param sql SQL query string with ? placeholders for parameters
 * @param params Optional array of parameters to bind to the query
 * @param returnMultiple If true, returns all matching rows; if false, returns single row
 * @returns Typed result(s) or null if no results
 */
export async function executeSql<T = any>(
  sql: string,
  params?: any[],
  returnMultiple: boolean = false
): Promise<T | T[] | null> {
  await waitForDB();
  if (!db) throw new Error('Database not initialized');
  
  // Use rowMode: 'object' to get rows as objects with named properties
  const result = db.exec({
    sql,
    bind: params || [],
    returnValue: 'resultRows',
    rowMode: 'object'
  });
  
  if (!result || result.length === 0) {
    // Check if this was a modification query and persist if needed
    const isModification = /^\s*(INSERT|UPDATE|DELETE|REPLACE|CREATE|DROP|ALTER)/i.test(sql);
    if (isModification) {
      await persistDB();
    }
    return null;
  }
  
  const results = result as T[];
  
  // Check if this was a modification query and persist if needed
  const isModification = /^\s*(INSERT|UPDATE|DELETE|REPLACE|CREATE|DROP|ALTER)/i.test(sql);
  if (isModification) {
    await persistDB();
  }
  
  if (returnMultiple) {
    return results.length > 0 ? results : null;
  } else {
    return results.length > 0 ? results[0] : null;
  }
}

/**
 * Export the current database as a downloadable SQLite file
 */
export async function exportDatabase(): Promise<Uint8Array> {
  await waitForDB();
  if (!db || !sqlite3) {
    throw new Error('Database not initialized');
  }
  
  try {
    // Allocate memory for the size output parameter
    const pSize = sqlite3.wasm.alloc(8);
    
    // Serialize the database
    const pDB = sqlite3.capi.sqlite3_serialize(
      db.pointer,
      'main',
      pSize,
      0
    );
    
    if (!pDB) {
      sqlite3.wasm.dealloc(pSize);
      throw new Error('Failed to serialize database for export');
    }
    
    // Read the size
    const size = sqlite3.wasm.peek(pSize, 'i64');
    
    // Create Uint8Array copy of the serialized data
    const heapView = sqlite3.wasm.heap8u();
    const binaryArray = new Uint8Array(Number(size));
    for (let i = 0; i < Number(size); i++) {
      binaryArray[i] = heapView[pDB + i];
    }
    
    // Free allocated memory
    sqlite3.capi.sqlite3_free(pDB);
    sqlite3.wasm.dealloc(pSize);
    
    return binaryArray;
  } catch (error) {
    console.error('Error exporting database:', error);
    throw error;
  }
}

/**
 * Get client options by field key
 * @param fieldKey The field key (e.g., 'countries', 'property_types')
 * @returns Array of option items with field_value, field_title, and price
 */
export async function getClientOptionsByKey(fieldKey: string): Promise<Array<{ field_value: string; field_title: string | null; price: number | null }> | null> {
  const results = await executeSql<{ field_value: string; field_title: string | null; price: number | null }>(
    'SELECT field_value, field_title, price FROM client_option_items WHERE field_key = ? ORDER BY id',
    [fieldKey],
    true
  );
  return results as Array<{ field_value: string; field_title: string | null; price: number | null }> | null;
}

/**
 * Get all client options 
 * @returns Array of option items grouped by field_key
 */
export async function getClientOptionsAll(): Promise<Array<{ field_key: string; field_value: string; field_title: string | null; price: number | null }> | null> {
  const results = await executeSql<{ field_key: string; field_value: string; field_title: string | null; price: number | null }>(
    'SELECT field_key, field_value, field_title, price FROM client_option_items ORDER BY field_key, id',
    [null],
    true
  );
  return results as Array<{ field_key: string; field_value: string; field_title: string | null; price: number | null }> | null;
}

/**
 * Get client options by wizard step
 * @param step The wizard step number
 * @returns Array of option items grouped by field_key
 */
export async function getClientOptionsByStep(step: number): Promise<Array<{ field_key: string; field_value: string; field_title: string | null; price: number | null }> | null> {
  const results = await executeSql<{ field_key: string; field_value: string; field_title: string | null; price: number | null }>(
    'SELECT field_key, field_value, field_title, price FROM client_option_items WHERE step = ? ORDER BY field_key, id',
    [step],
    true
  );
  return results as Array<{ field_key: string; field_value: string; field_title: string | null; price: number | null }> | null;
}


/**
 * Get the last sync timestamp for client options
 * @returns ISO timestamp string or null if never synced
 */
export async function deleteAllClientData(): Promise<void> {
  await executeSql('DELETE FROM orders');
  await executeSql('DELETE FROM images');
}


export async function resetPacksTable(): Promise<void> {

    console.log('DELETING packs records');
    await executeSql('DELETE FROM product_packs');

    const forceDel: Boolean = true;
    if(forceDel === true){
      console.log('Dropping pack table');
      db.exec(`
        DROP TABLE product_packs;
      `);

      db.exec(`
        CREATE TABLE IF NOT EXISTS product_packs_new (
            id INTEGER PRIMARY KEY AUTOINCREMENT,
            range_id INTEGER NOT NULL,
            pack_key TEXT NOT NULL,
            manditory INTEGER NOT NULL 
        );
      `);

      db.exec(`
        ALTER TABLE product_packs_new RENAME TO product_packs_alt;
      `);
    }
}

/**
 * Save client options to the database
 * @param last_sync The timestamp of the last sync
 * @param field_products Array of field groups with their data
 * @param catalogue Optional catalogue data for parent-child product relationships
 */
export async function saveClientOptions(
  clientOptions: ClientOptionsResponse
): Promise<void> {

  const { last_sync, field_products, catalogue, incompatibilities, packs } = clientOptions;

  await waitForDB();
  if (!db) {
    throw new Error('Database not initialized');
  }

  // Clear existing client options (full replace strategy)
  //console.log('Clearing existing client options...');
  await executeSql('DELETE FROM client_option_items');
  await executeSql('DELETE FROM client_options_sync');
  await executeSql('DELETE FROM product_catalogue');
  await executeSql('DELETE FROM product_incompatibility');
  await executeSql('DELETE FROM product_packs');


  // Insert sync timestamp
  await executeSql(
    'INSERT INTO client_options_sync (id, last_sync) VALUES (?, ?)',
    [1, last_sync]
  );

  // Insert all option items
  console.log(`Inserting ${field_products.length} option field groups...`);
  let totalItems = 0;
  
  for (const field of field_products) {
    const { key, step, options } = field;
    
    for (const item of options) {
      await executeSql(
        'INSERT INTO client_option_items (field_key, step, field_value, field_title, price) VALUES (?, ?, ?, ?, ?)',
        [key, step ?? null, String(item.value), item.title ?? null, item.price ?? null]
      );
      totalItems++;
    }
  }

  //console.log(`✓ Client options saved successfully. ${totalItems} items stored.`);

  // Insert catalogue data if provided
  if (catalogue && catalogue.length > 0) {
    //console.log(`Inserting ${catalogue.length} catalogue entries...`);
    let catalogueItems = 0;
    
    for (const entry of catalogue) {
      const { type, parent, children } = entry;
      
      // Normalize: create one row for each parent-child relationship
      for (const child of children) {
        await executeSql(
          'INSERT INTO product_catalogue (type, parent, child) VALUES (?, ?, ?)',
          [type, parent, child]
        );
        catalogueItems++;
      }
    }
    
    //console.log(`✓ Product catalogue saved successfully. ${catalogueItems} relationships stored.`);
  }

  if (incompatibilities) {
    //console.log('Inserting incompatibilities...');

    let totalRules = 0;

    // Option → Variant
    for (const rule of incompatibilities.option_variant || []) {
      await executeSql(
        `INSERT INTO product_incompatibility
        (option_id, incompatible_variant_id)
        VALUES (?, ?)`,
        [rule.optionId, rule.variantId]
      );
      totalRules++;
    }

    // Option → Option (store ONE direction, enforce symmetry in code)
    for (const rule of incompatibilities.option_option || []) {
      await executeSql(
        `INSERT INTO product_incompatibility
        (option_id, incompatible_option_id)
        VALUES (?, ?)`,
        [rule.optionId, rule.blockedWith]
      );
      totalRules++;
    }

    //console.log(`✓ ${totalRules} incompatibility rules saved`);
  }

  if (packs) {
    //console.log(`Inserting ${packs.length} product packs...`, packs);

    for (const pack of packs || []) {
      await executeSql(
        `INSERT INTO product_packs (range_id, pack_key, manditory) VALUES (?, ?, ?)`,
        [pack.rangeId, pack.packKey, pack.manditory]
      );
    }

    //console.log('✓ Product packs saved successfully');
  }


}

/**
 * Get child variant IDs for a given parent product range
 * @param parent The parent product range ID
 * @returns Array of child variant IDs or empty array if none found
 */
export async function getCatalogueChildren(type:string, parent: string): Promise<string[]> {
  const results = await executeSql<{ type:string, child: string }>(
    'SELECT child FROM product_catalogue WHERE type= ? AND parent = ? ORDER BY child',
    [type, parent],
    true
  );
  
  if (!results || !Array.isArray(results)) {
    return [];
  }
  
  return results.map(row => row.child);
}

export async function loadIncompatibilitiesOffline(): Promise<{
  optionVariant: Set<string>;
  optionOption: Set<string>;
}> {

  const rows = db.exec({
    sql: `SELECT option_id, incompatible_variant_id, incompatible_option_id FROM product_incompatibility`,
    rowMode: "object"
  });

  //const rows = extractRows(result);

  

  const optionVariant = new Set<string>();
  const optionOption = new Set<string>();

  if (!rows || !Array.isArray(rows)) {
    return { optionVariant, optionOption };
  }

  for (const row of rows) {
    if (row.incompatible_variant_id) {
      optionVariant.add(
        `${row.option_id}:${row.incompatible_variant_id}`
      );
    }

    if (row.incompatible_option_id) {
      optionOption.add(
        `${row.option_id}:${row.incompatible_option_id}`
      );
      optionOption.add(
        `${row.incompatible_option_id}:${row.option_id}`
      ); // enforce symmetry
    }
  }

  return { optionVariant, optionOption };
}

export async function loadPacksOffline(): Promise<ProductPackCache[]> {

  const rows = db.exec({
    sql: `SELECT range_id, pack_key, manditory FROM product_packs`,
    rowMode: "object"
  });
  
  if (!rows || !Array.isArray(rows)) {
    return [];
  }

  //const packs: Array<ProductPack> = [];
  //const rows = extractRows(result);
  // if(rows) {
    
  //   for (const row of rows) {

  //     packs.push({
  //       rangeId: row.range_id,
  //       packKey: row.pack_key,
  //       manditory: row.manditory
  //     });
  //   }
  // }
  const packsData: ProductPack[] = rows?.map((row) => {
    return {
      rangeId: row.range_id,
      packKey: row.pack_key,
      manditory: row.manditory
    }
  });

  return buildProductPackCache(packsData);
}

function buildProductPackCache(packs?: ProductPack[]): ProductPackCache[] {

    let cache: ProductPackCache[] = [];

    for (const row of packs || []) {

        let ppc: ProductPackCache | null = null;

        const rIdx = cache.findIndex(x => x.rangeId === row.rangeId);
        const isNewPack: boolean = !(rIdx > -1);
        if (!isNewPack) {
            ppc = cache[rIdx];
        }
        else {
            ppc = {
                rangeId: row.rangeId,
                categories: []
            };
        }

        const pCategory: PackCategory = {
            packKey: row.packKey,
            manditory: row.manditory
        };

        //Always add the category
        ppc?.categories!.push(pCategory);

        if(!isNewPack){
            cache[rIdx] = ppc;
        }
        else{
            cache.push(ppc);
        }
    }
    return cache;
}

// function extractRows(result: any): any[] {
//   if (!result) return [];

//   // Already an array
//   if (Array.isArray(result)) return result;

//   // Common wrappers
//   if (Array.isArray(result.rows)) return result.rows;
//   if (Array.isArray(result.resultRows)) return result.resultRows;
//   if (Array.isArray(result.values)) return result.values;

//   // SINGLE ROW OBJECT 
//   if (typeof result === "object") 
//     return [result];

//   return [];
// }


/**
 * Get count of images for a specific order and step
 * @param orderId The order ID
 * @param step The wizard step number
 * @returns Number of images
 */
export async function getImageCount(orderId: number, step: number): Promise<number> {
  await waitForDB();
  if (!db) throw new Error('Database not initialized');
  
  const result = db.exec({
    sql: 'SELECT COUNT(*) as count FROM images WHERE order_id = ? AND step = ?',
    bind: [orderId, step],
    returnValue: 'resultRows',
    rowMode: 'object'
  });
  
  if (!result || result.length === 0) {
    return 0;
  }
  
  return result[0].count || 0;
}

/**
 * Check if an image exists locally by server ID
 * @param serverId The server image ID
 * @param orderId The order ID
 * @returns Image object if found, null otherwise
 */
export async function getImageByServerId(serverId: string, orderId: number): Promise<{ id: number; blob: Uint8Array; filename: string } | null> {
  await waitForDB();
  if (!db) throw new Error('Database not initialized');
  
  const result = db.exec({
    sql: 'SELECT id, blob, filename FROM images WHERE server_id = ? AND order_id = ? AND deleted_at IS NULL',
    bind: [serverId, orderId],
    returnValue: 'resultRows',
    rowMode: 'array'
  });
  
  if (!result || result.length === 0) {
    return null;
  }
  
  const row = result[0];
  return {
    id: row[0] as number,
    blob: new Uint8Array(row[1] as ArrayBuffer),
    filename: row[2] as string
  };
}

/**
 * Save a downloaded image from the server with its server ID
 * @param orderId The order ID
 * @param step The wizard step number
 * @param serverId The server image ID
 * @param blob The image blob data
 * @param filename The image filename
 * @param tag The image tag/label
 */
export async function saveDownloadedImage(orderId: number, step: number, serverId: string, blob: Uint8Array, filename: string, tag: string): Promise<void> {
  await waitForDB();
  if (!db) throw new Error('Database not initialized');
  
  db.exec({
    sql: 'INSERT INTO images (order_id, step, blob, filename, server_id, synced_at, tag) VALUES (?, ?, ?, ?, ?, datetime("now"), ?)',
    bind: [orderId, step, blob, filename, serverId, tag]
  });
  
  await persistDB();
  console.log(`[saveDownloadedImage] Downloaded image saved for order ${orderId}, step ${step}, server_id ${serverId}, tag: ${tag}`);
}

/**
 * Get all images that have been deleted locally but need to be deleted on the server
 * @returns Array of images with server_id that are marked for deletion
 */
export async function getDeletedImages(): Promise<Array<{ id: number; server_id: string; order_id: number }> | null> {
  const results = await executeSql<{ id: number; server_id: string; order_id: number }>(
    'SELECT id, server_id, order_id FROM images WHERE deleted_at IS NOT NULL AND server_id IS NOT NULL',
    [],
    true
  );
  return results as Array<{ id: number; server_id: string; order_id: number }> | null;
}

/**
 * Permanently delete an image record from the database after successful server deletion
 * @param imageId The ID of the image to permanently delete
 */
export async function permanentlyDeleteImage(imageId: number): Promise<void> {
  await waitForDB();
  if (!db) throw new Error('Database not initialized');
  
  db.exec({
    sql: 'DELETE FROM images WHERE id = ?',
    bind: [imageId]
  });
  
  await persistDB();
  console.log(`[permanentlyDeleteImage] Image ${imageId} permanently deleted from database`);
}

/**
 * Mark an order for deletion (soft-delete)
 * @param orderId The ID of the order to mark
 * @param timestamp ISO timestamp string when the order was marked
 */
export async function markOrderForDeletion(orderId: number, timestamp: string): Promise<void> {
  await executeSql(
    'UPDATE orders SET marked_for_deletion = ? WHERE id = ?',
    [timestamp, orderId]
  );
  console.log(`[markOrderForDeletion] Order ${orderId} marked for deletion at ${timestamp}`);
}

/**
 * Get sync metadata (last server time and sync date)
 * @returns Sync metadata object or null if never synced
 */
export async function getSyncMetadata(): Promise<{ last_server_time: string; last_sync_date: string } | null> {
  const result = await executeSql<{ last_server_time: string; last_sync_date: string }>(
    'SELECT last_server_time, last_sync_date FROM sync_metadata WHERE id = 1',
    [],
    false
  );
  return result as { last_server_time: string; last_sync_date: string } | null;
}

/**
 * Save sync metadata (server time and current sync date)
 * @param serverTime The server time from the sync response
 */
export async function saveSyncMetadata(serverTime: string): Promise<void> {
  const currentTime = new Date().toISOString();
  
  // Use INSERT OR REPLACE to update existing record or create new one
  await executeSql(
    'INSERT OR REPLACE INTO sync_metadata (id, last_server_time, last_sync_date) VALUES (?, ?, ?)',
    [1, serverTime, currentTime]
  );
  //console.log(`[saveSyncMetadata] Sync metadata saved: server_time=${serverTime}, sync_date=${currentTime}`);
}

/**
 * Get all orders that are marked for deletion
 * @returns Array of orders marked for deletion
 */
export async function getOrdersMarkedForDeletion(): Promise<Order[]> {
  const orders = await querySqlite<Order>(
    'SELECT * FROM orders WHERE marked_for_deletion IS NOT NULL ORDER BY marked_for_deletion DESC',
    [],
    true,
    OrderModel
  );
  return orders as Order[] || [];
}

/**
 * Permanently delete an order marked for deletion (user-initiated cleanup)
 * @param orderId The ID of the order to permanently delete
 */
export async function permanentlyDeleteMarkedOrder(orderId: number): Promise<void> {
  await deleteOrder(orderId);
  console.log(`[permanentlyDeleteMarkedOrder] Order ${orderId} and its images permanently deleted`);
}

export { db };
